import { DateTime } from "luxon";
import { HolidayManager } from "../../../../pure-javascript-api/ti-js-api";

declare const TIJSAPI: any;

export function getHolidayManager() {
    let holidayManager = null;
    if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
        holidayManager = TIJSAPI.HolidayManager;
    } else {
        holidayManager = HolidayManager;
    }

    return holidayManager;
}

export function getHolidayManagerInstance() {
    let holidayManager: HolidayManager = null as any;
    if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
        holidayManager = TIJSAPI.HolidayManager.getInstance();
    } else {
        holidayManager = HolidayManager.getInstance();
    }

    return holidayManager;
}

export class DateHelpers {
    public static fullMonths: Array<string> = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    public static shortMonths: Array<string> = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
    ];

    public static shortDays: Array<string> = [
        'Sun',
        'Mon',
        'Tue',
        'Wed',
        'Thu',
        'Fri',
        'Sat'
    ];

    public static fullDays: Array<string> = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Satuarday'
    ];

    public static addMonths(date: Date, month: number) {
        date.setMonth(date.getMonth() + month);
        return date;
    }

    public static addDays(date: Date, days: number) {
        date.setDate(date.getDate() + days);
        return date;
    }

    public static addMinutes(date: Date, minutes: number) {
        date.setMinutes(date.getMinutes() + minutes);
        return date;
    }

    public static getHoursDifferent(first: Date, second: Date): number {
        return Math.abs(first.valueOf() - second.valueOf()) / 36e5;
    }

    public static removeTime(date: Date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    }

    public static getTimeStampWithCurrentTimezone(date: Date) {
        return date.valueOf() - 60000 * date.getTimezoneOffset();
    }

    public static format(format: string, date: Date): string {
        const dateString = format
            .replace('yyyy', date.getFullYear() + '')
            .replace('mm', (date.getMonth() + 1) <= 9 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1) + '')
            .replace('SM', DateHelpers.shortMonths[date.getMonth()])
            .replace('FM', DateHelpers.fullMonths[date.getMonth()])

            .replace('dd', date.getDate() <= 9 ? '0' + date.getDate() : date.getDate() + '')

            .replace('HH', date.getHours() <= 9 ? '0' + date.getHours() : date.getHours() + '')
            .replace('SH', DateHelpers.getShortHours(date.getHours()))

            .replace('MM', date.getMinutes() <= 9 ? '0' + date.getMinutes() : date.getMinutes() + '')
            .replace('SS', date.getSeconds() <= 9 ? '0' + date.getSeconds() : date.getSeconds() + '')

            .replace('tt', date.getHours() >= 12 ? 'PM' : 'AM')
            
            .replace('yy', (date.getFullYear() + '').substring(2, 4))
            .replace('m', (date.getMonth() + 1) + '')
            .replace('d', date.getDate() + '')

            .replace('SD', DateHelpers.shortDays[date.getDay()]);

        return dateString;
    }

    public static addBusinessDay(source: Date, businessDays: number): Date {
        const dayOfWeek = businessDays < 0 ? (source.getDay() - 12) % 7 : (source.getDay() + 6) % 7;
        switch (dayOfWeek) {
            case 6:
                businessDays--;
                break;
            case -6:
                businessDays++;
                break;
        }

        const dayToAdd = businessDays + parseInt((parseInt(((businessDays + dayOfWeek) / 5) + '') * 2) + '');
        return DateHelpers.addDays(source, dayToAdd);
    }

    public static addWeekDays(date: DateTime, days: number): DateTime {
        if (days <= 0) {
            return date;
        }

        if (date.weekday == 6) {
            date = date.plus({ days: 2 });
            days -= 1;
        }
        else if (date.weekday == 7) {
            date = date.plus({ days: 1 });
            days -= 1;
        }

        date = date.plus({ days: days / 5 * 7 });
        let extraDays = days % 5;

        if (date.day + extraDays > 5) {
            extraDays += 2;
        }

        return date.plus({ days: extraDays });
    }

    public static getTraceLoggingTimeStamp(): string {
        const currentDate = new Date();

        const padWithZeros = (number: number, length: number) => {
            return String(number).padStart(length, '0');
        }
        // Extract individual components
        const year = currentDate.getFullYear();
        const month = padWithZeros(currentDate.getMonth() + 1, 2); // Adding 1 to month since it is zero-based
        const day = padWithZeros(currentDate.getDate(), 2);
        const hours = padWithZeros(currentDate.getHours(), 2);
        const minutes = padWithZeros(currentDate.getMinutes(), 2);
        const seconds = padWithZeros(currentDate.getSeconds(), 2);
        const milliseconds = padWithZeros(currentDate.getMilliseconds(), 3);

        // Determine if it's AM or PM
        const meridiem = parseFloat(hours) >= 12 ? 'PM' : 'AM';

        // Convert 24-hour format to 12-hour format
        const hours12 = padWithZeros(parseFloat(hours) % 12 || 12, 2);

        // Create the formatted date string
        const formattedDate = `${month}-${day}-${year} ${hours12}:${minutes}:${seconds}.${milliseconds} ${meridiem}`;
        return `${formattedDate}`;
    }

    public static toMarketTimeZone(date: Date): DateTime {
        return DateTime.fromJSDate(date, {
            zone: 'America/New_York'
        });
    }

    public static isMarketOpenNow(isDemo: boolean): boolean {
        let date = new Date();
        if (isDemo) {
            date = DateHelpers.addMinutes(date, -15);
        }

        return !DateHelpers.isPremarket(date) && !DateHelpers.isPostmarket(date); 
    }

    public static isMarketOpen(date: Date): boolean {
        return !DateHelpers.isPremarket(date) && !DateHelpers.isPostmarket(date); 
    }

    public static getMarketOpenInLocalTime(value: Date): Date {
        let holidayManager = getHolidayManagerInstance();

        const marketOpen = holidayManager.getMarketOpenInMarketTimeZone(value);
        return marketOpen.toLocal().toJSDate();
    }
    
    public static getMarketCloseLocalTime(value: Date): Date {
        let holidayManager = getHolidayManagerInstance();
        const marketOpen = holidayManager.getMarketOpenInMarketTimeZone(value);
        const marketClose = holidayManager.getMarketCloseInMarketTimeZone(marketOpen.toJSDate());
        return marketClose.toLocal().toJSDate();
    }
    
    public static isPremarket(value: Date): boolean {
        const holidayManager = getHolidayManagerInstance();

        const marketSchedule = holidayManager.getMarketSchedule();
        const marketOpen = holidayManager.getMarketOpenInMarketTimeZone(value);
        const preMarketOpen = marketOpen.minus({minute: marketSchedule.preMarketMinutes});
        const currentStartValue = DateHelpers.toMarketTimeZone(value);
        const isPremarket = currentStartValue.toUnixInteger() >= preMarketOpen.toUnixInteger() && currentStartValue.toUnixInteger() < marketOpen.toUnixInteger();
        return isPremarket;
    }

    public static isPostmarket(value: Date): boolean {
        const holidayManager = getHolidayManagerInstance();
        
        const marketSchedule = holidayManager.getMarketSchedule();
        const marketClose = holidayManager.getMarketCloseInMarketTimeZone(value);
        const currentStartValue = DateHelpers.toMarketTimeZone(value);
        const postMarketClose = marketClose.plus({minute: marketSchedule.postMarketMinutes});
        
        // const marketOpen = holidayManager.getMarketOpenInMarketTimeZone(value);
        // const preMarketOpen = marketOpen.minus({minute: marketSchedule.preMarketMinutes});
        const isPostMarket = currentStartValue.toUnixInteger() >= marketClose.toUnixInteger() && currentStartValue.toUnixInteger() < postMarketClose.toUnixInteger();
        return isPostMarket;
    }
    
    public static isNowMarketTimeZoneAfterHousrs(val: number): boolean {
        const nowInMarketTimeZone = this.toMarketTimeZone(new Date());
        const beforeAfterNoon = nowInMarketTimeZone.set({ hour: val, minute: 0, second: 0, millisecond: 0 });

        return nowInMarketTimeZone > beforeAfterNoon;
    }

    public static getPreMarketOpenTime(date: Date): DateTime {
        const marketTimeZoneNow = this.toMarketTimeZone(date);
        const marketSchedule = getHolidayManagerInstance().getMarketSchedule();
        
        const minutesPreMarketOpen = marketSchedule.minutesUntilOpen - marketSchedule.preMarketMinutes;
        return marketTimeZoneNow.startOf('day').plus({ minute: minutesPreMarketOpen });
    }

    public static getMarketCloseTime(date: Date): DateTime {
        let holidayManager = getHolidayManagerInstance();
        
        const marketTimeZoneNow = this.toMarketTimeZone(date);
        const marketClose = holidayManager.getMarketCloseInMarketTimeZone(marketTimeZoneNow.toJSDate());
        return marketClose;
    }

    public static getPostMarketCloseTime(date: Date): DateTime {
        const postMarketOpenTime = this.getMarketCloseTime(date);
        const postMarketCloseTime = postMarketOpenTime.plus({ minutes: this.getPostMarketMinutes()});
        return postMarketCloseTime;
    }
    
    public static getPostMarketMinutes() {
        let holidayManager = getHolidayManagerInstance();
        return holidayManager.getPostMarketMinutes();
    }

    public static getMarketOpenTime(date: Date): DateTime {
        const marketSchedule = getHolidayManagerInstance().getMarketSchedule();
        const marketTimeZoneNow = this.toMarketTimeZone(date);
        return marketTimeZoneNow.startOf('day').plus({ minute: marketSchedule.minutesUntilOpen });
    }
    
    public static convertFromEasternTime(date: Date, zone: string): DateTime {
        const dateTime =  DateTime.fromJSDate(date, {
            zone: 'America/New_York'
        });

        if (!zone) {
            return dateTime;
        }

        return dateTime.setZone(zone);
    }

    public static convertFromTimezone(date: Date, zone: string): DateTime {
        const dateTime =  DateTime.fromJSDate(date, {
            zone: zone
        });
        return dateTime;
    }

    public static isNormalWorkingDay(currentTime: DateTime) {
        return !DateHelpers.isWeekend(currentTime) && !DateHelpers.isHoliday(currentTime);
    }

    public static isWeekend(date: DateTime): boolean {
        return date.weekday == 6 || date.weekday == 7;
    }

    public static isHoliday(date: DateTime): boolean {
        let holidayManager = getHolidayManagerInstance();
        
        return holidayManager.isHolidayMarketClosed(date.toJSDate());
    }

    public static getNextWeekday(date: DateTime): DateTime {
        if (date.weekday == 6)
            date = date.plus({ days: 2 });
        if (date.weekday == 7)
            date = date.plus({ days: 1 });
        return date;
    }

    public static toTimeT(date: Date): number {
        const dateTime = this.toMarketTimeZone(date);
        return dateTime.toUnixInteger();
    }

    private static getShortHours(hours: number): string {
        let h = hours % 12;
        if (h > 0) {
            if (h <= 9) {
                return '0' + h;
            }

            return h + '';
        }

        return '12';
    }
}
